/**
 * @fileoverview A function that provides the staff API.
 *
 * @example
 * const { result } = renderHook(() => useStaffAPI())
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import { axiosBaseQuery } from './utils/axiosBaseQuery'

export const staffApi = createApi({
    reducerPath: 'staffApi',
    tagTypes: ['Staff', 'StaffCounter'],
    baseQuery: axiosBaseQuery({ baseUrl: `/private/users` }),
    endpoints: (builder) => ({
        getStaff: builder.query<Novoic.API.Staff[], void>({
            query: () => {
                const sp = new URLSearchParams(location.search)
                return {
                    url: location.search ? `?${sp.toString()}` : '',
                    method: 'GET',
                }
            },
            providesTags: (result) =>
                result
                    ? [...result.map(({ id }) => ({ type: 'Staff' as const, id })), 'Staff']
                    : ['Staff'],
        }),

        getStaffById: builder.query<Novoic.API.Staff, string>({
            query: (id: string) => {
                return {
                    url: `/${id}`,
                    method: 'GET',
                }
            },
            providesTags: (result) => [{ type: 'Staff' as const, id: result?.id }],
        }),

        getStaffCount: builder.query<{ count: number }, void>({
            query: () => {
                const sp = new URLSearchParams(location.search)
                return {
                    url: location.search ? `/count?${sp.toString()}` : '/count',
                    method: 'GET',
                }
            },
        }),

        getStaffForSiteId: builder.query<Novoic.API.Staff[], string>({
            query: (siteId: string) => {
                const sp = new URLSearchParams(location.search)
                sp.set('sites', siteId)
                return {
                    url: `?${sp.toString()}`,
                    method: 'GET',
                }
            },
        }),

        getStaffCountForSiteId: builder.query<{ count: number }, string>({
            query: (siteId: string) => {
                const sp = new URLSearchParams(location.search)
                sp.set('sites', siteId)
                return {
                    url: `/count?${sp.toString()}}`,
                    method: 'GET',
                }
            },
        }),

        deactivate: builder.mutation<void, string>({
            query: (user_id) => ({
                url: `/${user_id}/block`,
                method: 'POST',
            }),
        }),

        reactivate: builder.mutation<void, string>({
            query: (user_id) => ({
                url: `/${user_id}/block`,
                method: 'DELETE',
            }),
        }),
    }),
})

export const {
    useGetStaffQuery,
    useGetStaffByIdQuery,
    useGetStaffCountForSiteIdQuery,
    useGetStaffForSiteIdQuery,
    useGetStaffCountQuery,
    useDeactivateMutation,
    useReactivateMutation,
} = staffApi
